import {
  APPROACH,
  multi,
  NEEDLE_GAUGE,
  number,
  OTHER_TEXT,
  option,
  PRESENT_ABSENT_NOT_ASSESSED,
  type ProcedureDefinition,
  routine,
  SHARED_FIELD_IDS,
  SHARED_FIELDS,
  single,
  text,
  YES_PARTIAL_NO,
} from "./procedure-fields";

type JointSize = "small" | "intermediate" | "major";

const CPT_BY_JOINT_SIZE: Record<JointSize, string> = {
  small: "20604",
  intermediate: "20606",
  major: "20611",
};

const JOINT_SIZE_BY_SITE: Record<string, JointSize | null> = {
  knee: "major",
  shoulder_glenohumeral: "major",
  shoulder_subacromial_bursa: "major",
  hip: "major",
  greater_trochanteric_bursa: "major",
  elbow: "intermediate",
  wrist: "intermediate",
  ankle: "intermediate",
  acromioclavicular: "intermediate",
  olecranon_bursa: "intermediate",
  mcp: "small",
  pip_dip: "small",
  mtp: "small",
  other: null,
};

const ASPIRATION = {
  field: "procedure_type",
  in: ["aspiration", "aspiration_and_injection"],
};
const INJECTION = {
  field: "procedure_type",
  in: ["injection", "aspiration_and_injection"],
};

export const GUIDED_JOINT_INJECTION: ProcedureDefinition = {
  examType: "guided_joint_injection",
  version: 1,
  allNormal: true,
  indications: [
    option(
      "diagnostic_aspiration_septic",
      "Diagnostic aspiration (suspected septic arthritis)",
    ),
    option(
      "diagnostic_aspiration_crystal",
      "Diagnostic aspiration (suspected crystal arthritis)",
    ),
    option(
      "therapeutic_aspiration_effusion",
      "Therapeutic aspiration (symptomatic effusion)",
    ),
    option(
      "therapeutic_injection_osteoarthritis",
      "Therapeutic injection (osteoarthritis)",
    ),
    option(
      "therapeutic_injection_inflammatory",
      "Therapeutic injection (inflammatory arthritis)",
    ),
    option(
      "therapeutic_injection_bursitis",
      "Therapeutic injection (bursitis)",
    ),
    option("other", "Other"),
  ],
  views: [
    option("pre_target_longitudinal", "Pre-procedure target, longitudinal"),
    option("pre_target_transverse", "Pre-procedure target, transverse"),
    option("doppler_needle_path", "Doppler (vessel check along needle path)"),
    option("needle_in_target_in_plane", "Needle-in-target, in-plane"),
    option("needle_in_target_out_of_plane", "Needle-in-target, out-of-plane"),
    option("post_procedure", "Post-procedure (injectate distribution)"),
  ],
  fields: [
    ...SHARED_FIELDS,
    single("site", "Site (joint size category)", [
      option("knee", "Knee (Major)"),
      option("shoulder_glenohumeral", "Shoulder glenohumeral (Major)"),
      option(
        "shoulder_subacromial_bursa",
        "Shoulder subacromial-subdeltoid bursa (Major)",
      ),
      option("hip", "Hip (Major)"),
      option(
        "greater_trochanteric_bursa",
        "Greater trochanteric bursa (Major)",
      ),
      option("elbow", "Elbow (Intermediate)"),
      option("wrist", "Wrist (Intermediate)"),
      option("ankle", "Ankle (Intermediate)"),
      option("acromioclavicular", "Acromioclavicular joint (Intermediate)"),
      option("olecranon_bursa", "Olecranon bursa (Intermediate)"),
      option("mcp", "MCP joint (Small)"),
      option("pip_dip", "PIP / DIP joint (Small)"),
      option("mtp", "MTP joint (Small)"),
      OTHER_TEXT,
    ]),
    single(
      "joint_size",
      "Joint size",
      [
        option("major", "Major (e.g. knee, shoulder, hip)"),
        option("intermediate", "Intermediate (e.g. elbow, wrist, ankle)"),
        option("small", "Small (e.g. finger, toe)"),
      ],
      { field: "site", in: ["other"] },
    ),
    single("procedure_type", "Procedure type", [
      option("aspiration", "Aspiration"),
      option("injection", "Injection"),
      option("aspiration_and_injection", "Aspiration and injection"),
    ]),
    single(
      "pre_procedure_effusion",
      "Pre-procedure effusion",
      PRESENT_ABSENT_NOT_ASSESSED,
    ),
    APPROACH,
    single("approach_aspect", "Approach aspect", [
      option("anterior", "Anterior"),
      option("posterior", "Posterior"),
      option("medial", "Medial"),
      option("lateral", "Lateral"),
      option("superior", "Superior"),
      OTHER_TEXT,
    ]),
    NEEDLE_GAUGE,
    single(
      "needle_tip_visualized",
      "Needle tip visualized in target",
      YES_PARTIAL_NO,
    ),
    number("aspirate_volume", "Aspirate volume", "ml", {
      min: 0,
      showWhen: ASPIRATION,
    }),
    single(
      "aspirate_appearance",
      "Aspirate appearance",
      [
        option("clear_straw", "Clear / straw"),
        option("turbid", "Turbid"),
        option("purulent", "Purulent"),
        option("blood_stained", "Blood-stained"),
        option("hemarthrosis", "Hemarthrosis"),
        option("dry_tap", "Dry tap"),
        option("not_applicable", "Not applicable"),
      ],
      ASPIRATION,
    ),
    multi(
      "samples_sent",
      "Samples sent",
      [
        option("microscopy_culture", "Microscopy and culture"),
        option("crystals", "Crystals"),
        option("cell_count", "Cell count"),
        option("none", "None"),
      ],
      ASPIRATION,
    ),
    text("injectate", "Injectate (drug and dose)", INJECTION),
    text("local_anesthetic", "Local anesthetic (drug and volume)", INJECTION),
    number("total_volume_injected", "Total volume injected", "ml", {
      min: 0,
      showWhen: INJECTION,
    }),
    routine(
      "immediate_complications",
      "Immediate complications",
      [
        option("none", "None"),
        option("vasovagal", "Vasovagal"),
        option("bleeding_hematoma", "Bleeding / hematoma"),
        OTHER_TEXT,
      ],
      "none",
    ),
  ],
  siteFieldId: "site",
  needleTipFieldId: "needle_tip_visualized",
  completeWhenAnswered: [
    ...SHARED_FIELD_IDS,
    "site",
    "procedure_type",
    "needle_tip_visualized",
  ],
  codesBySite: Object.fromEntries(
    Object.entries(JOINT_SIZE_BY_SITE).map(([site, size]) => [
      site,
      size ? [CPT_BY_JOINT_SIZE[size]] : [],
    ]),
  ),
  codesFromField: {
    whenSite: "other",
    fieldId: "joint_size",
    codes: Object.fromEntries(
      Object.entries(CPT_BY_JOINT_SIZE).map(([size, cpt]) => [size, [cpt]]),
    ),
  },
};
